import { LoaderWrapper, classNames, drop } from '@fl/cmsch-fe-library';
import { iframeResizer } from 'iframe-resizer';
import { maxBy, filter } from 'lodash/fp';
import React, { ComponentType, ReactElement, useCallback, useEffect, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Location, useLocation } from 'react-router';
import { flow, Opt, opt } from 'ts-opt';
import { Language } from 'api/gen/Language';
import { Config } from 'app/config';
import { Content } from 'app/content';
import { getGTagIds } from 'app/gtag';
import { legacyPaths, LegacyPath } from 'app/navigation';
import { logger } from 'app/sentry-logger';
import { State } from 'app/setup';
import { WithTranslation } from 'app/translations/with-translation';
import { legacyAction } from '../model';
import { genGTagMessageForLegacyBe } from '../utils/gen-g-tag-message-for-legacy-be';
import styles from './styles.sass';

/** Props shared by the legacy iframe view and its Redux-connected wrapper. */
interface LegacyIframeBaseProps extends WithTranslation<'common/urlTitles'> {
  /** Whether the iframe content is still loading. */
  iframeLoading: boolean;
  /** Indicates whether statistical cookies were accepted by the user. */
  statisticalCookieEnabled: boolean;
  /** Current application language used to rebuild legacy URLs after language changes. */
  language: Language;
  /** Current router location used to resolve the legacy target path. */
  location: Location;
  /** Dispatches the action that resets the legacy screen state. */
  resetScreen(): void;
  /** Dispatches the action that toggles the iframe loading flag. */
  setIframeLoading(value: boolean): void;
}
type LegacyIframeProps = WithTranslation<'common/urlTitles'>;
const iframeLoadedTimeout = 200;

/**
 * Finds the longest legacy route prefix that matches the current pathname.
 *
 * @param pathname Current front-end pathname.
 *
 * @returns Function that searches the provided legacy route list for the best match.
 */
export const getLongestMatchingLegacyPath: (pathname: string) => (legacyPaths: ReadonlyArray<LegacyPath>) => Opt<LegacyPath> = pathname => flow(filter((x: LegacyPath) => pathname.startsWith(x.path)), maxBy((x: LegacyPath) => x.path.length), opt);
interface ProcessPathInput {
  pathname: string;
  search: string;
  legacyPathsArray: ReadonlyArray<LegacyPath>;
}

/**
 * Resolves the legacy iframe URL for the current front-end route.
 *
 * @param pathname Current front-end pathname.
 * @param search Current query string, including the leading `?` when present.
 * @param legacyPathsArray Legacy route mappings used to find the target prefix.
 *
 * @returns Absolute legacy iframe URL.
 */
export const processLegacyPath = ({
  pathname,
  search,
  legacyPathsArray
}: ProcessPathInput): string => {
  const longestMatchingLegacyPath = getLongestMatchingLegacyPath(pathname)(legacyPathsArray).orCrash('legacy path for iframe not found');
  const restOfPathname = drop(longestMatchingLegacyPath.path.length)(pathname);
  const {
    legacyPath
  } = longestMatchingLegacyPath;
  const completeLegacyPath = `${Config.legacyEskotUrl}${legacyPath}${restOfPathname}${search}`;
  return completeLegacyPath;
};

/**
 * Renders the legacy iframe bridge and keeps its URL and loading state in sync with the app.
 *
 * @param props Legacy iframe view props.
 *
 * @returns Legacy iframe screen element.
 */
export const LegacyIframeBase = (props: LegacyIframeBaseProps): ReactElement => {
  const {
    iframeLoading,
    language,
    location: {
      pathname,
      search
    },
    resetScreen,
    setIframeLoading,
    statisticalCookieEnabled,
    t
  } = props;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previousPathnameRef = useRef(pathname);
  const previousLanguageRef = useRef(language);
  const previousStatisticalCookieEnabledRef = useRef(statisticalCookieEnabled);
  const completeLegacyPath = processLegacyPath({
    pathname,
    search,
    legacyPathsArray: legacyPaths
  });
  useEffect(() => {
    resetScreen();
  }, [resetScreen]);
  useEffect(() => {
    if (pathname !== previousPathnameRef.current) {
      setIframeLoading(true);
    }
    if (language !== previousLanguageRef.current || statisticalCookieEnabled && !previousStatisticalCookieEnabledRef.current) {
      setIframeLoading(true);
      if (iframeRef.current) {
        // eslint-disable-next-line no-self-assign
        iframeRef.current.src = iframeRef.current.src; // causes reload with new locale
      }
    }
    previousPathnameRef.current = pathname;
    previousLanguageRef.current = language;
    previousStatisticalCookieEnabledRef.current = statisticalCookieEnabled;
  }, [language, pathname, setIframeLoading, statisticalCookieEnabled]);
  const handleIframeLoaded = useCallback((): void => {
    setTimeout(() => setIframeLoading(false), iframeLoadedTimeout);
    if (iframeRef.current) {
      // @ts-expect-error iframeResizer is not typed correctly
      iframeResizer({
        warningTimeout: 0
      }, iframeRef.current);
    }
    if (statisticalCookieEnabled) {
      getGTagIds().then(gTagIds => {
        if (gTagIds.isSome() && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(genGTagMessageForLegacyBe(gTagIds.value), Config.legacyEskotUrl);
        }
      })
      // eslint-disable-next-line @fl/use-eta-reduction
      .catch(error => logger.logError(error));
    }
  }, [setIframeLoading, statisticalCookieEnabled]);
  return <Content havePadding noFooter={iframeLoading} flex="column"
  // @ts-expect-error its based on iframe URLs
  browserTabTitle={t(pathname)} data-sentry-element="Content" data-sentry-component="LegacyIframeBase" data-sentry-source-file="legacy-iframe.tsx">
            {iframeLoading && <LoaderWrapper loading stretch size="large" text={`${t('loading')}`} />}
            <iframe ref={iframeRef} src={completeLegacyPath} className={classNames(styles.legacyIframe, iframeLoading && 'invisible')} onLoad={handleIframeLoaded} />
        </Content>;
};

/**
 * Connects the legacy iframe screen to routing, translation, and Redux state.
 *
 * @returns Connected legacy iframe screen component.
 */
const LegacyIframeConnectedBase = ({
  t
}: LegacyIframeProps): ReactElement => {
  const dispatch = useDispatch();
  const location = useLocation();
  const iframeLoading = useSelector((state: State) => state.legacy.iframeLoading);
  const statisticalCookieEnabled = useSelector((state: State) => state.layout.statisticalCookieEnabled);
  const language = useSelector((state: State) => state.user.language);
  const resetScreen = useCallback((): void => {
    dispatch(legacyAction.resetScreen());
  }, [dispatch]);
  const setIframeLoading = useCallback((value: boolean): void => {
    dispatch(legacyAction.setIframeLoading(value));
  }, [dispatch]);
  return <LegacyIframeBase iframeLoading={iframeLoading} language={language} statisticalCookieEnabled={statisticalCookieEnabled} location={location} resetScreen={resetScreen} setIframeLoading={setIframeLoading} t={t} data-sentry-element="LegacyIframeBase" data-sentry-component="LegacyIframeConnectedBase" data-sentry-source-file="legacy-iframe.tsx" />;
};

/** Default legacy iframe screen used by the legacy route bridge. */
export const LegacyIframe: ComponentType = withTranslation('common/urlTitles')(LegacyIframeConnectedBase);